import { useState, useEffect, forwardRef } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  ListSubheader,
  TextField,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getTeams } from 'src/services/projectService';
import { getAllEmployees } from 'src/services/employeeService';
import { ResourceProps } from 'src/models/timesheet';

interface ResourcesMultiSelectProps {
  value: ResourceProps[],
  onChange: (e: any) => void,
  projectId: string,
  titile?: string,
  disabled?: boolean,
  getAll: boolean,
}

const ResourcesMultiSelect = forwardRef((props: ResourcesMultiSelectProps, _ref) => {
  const [list, setList] = useState([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>('');

  const fetchLists = () => {
    getTeams(props?.projectId).then((res) => {
      const data = res?.data;
      const currentDate = new Date();
      const currentTeams = data.flatMap(e => e.resourceAllocations);

      let resources = currentTeams?.filter(e => {
        if(e !== undefined){
          const start = new Date(e.startDate);
          const end = new Date(e.endDate);
          return currentDate < end && currentDate > start;
        }
      });

      resources = resources.map(resource => {
        return {
            ...resource,
            isAllocatedResource: true
        };
      });
      setList(resources);
    });
  }

  const fetchResources = (searchText) => {
    getAllEmployees().then((res) => {
      let sortedData = res.data.filter(e => !e?.employeeFinance?.isResign).sort((a, b) =>
      b.createdOn.localeCompare(a.createdOn));

      sortedData = sortedData.map(resource => {
        return {
            ...resource,
            isAllocatedResource: false
        };
      });
      setList(sortedData?.filter((option) => containsText(option?.name, searchText)));
    });
  }

  const containsText = (text, searchText) =>
    text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

  useEffect(() => {
    if(props?.projectId){
      if(searchText.length > 0){
        getTeams(props?.projectId).then((res) => {
          const data = res?.data;
          const currentDate = new Date();
          const currentTeams = data.flatMap(e => e.resourceAllocations);
    
          let resources = currentTeams?.filter(e => {
            if(e !== undefined){
              const start = new Date(e.startDate);
              const end = new Date(e.endDate);
              return currentDate < end && currentDate > start;
            }
          });
    
          resources = resources.map(resource => {
            return {
                ...resource,
                isAllocatedResource: true
            };
          });

          if(props?.getAll){
            if(resources.filter((option) => containsText(option?.name, searchText)).length === 0){
              fetchResources(searchText);
            } else{
              setList(resources.filter((option) => containsText(option?.name, searchText)));
            }
          } else{
            setList(resources.filter((option) => containsText(option?.name, searchText)));
          }
        });
      } else{
        fetchLists();
      }
    }
  }, [searchText]);

  useEffect(() => {
    const selectedName = [];
    if(props?.value && props?.value.length > 0){
      for(let i=0; i < props?.value.length; i++){
        selectedName.push(props?.value[i]?.name);
      }
    }
    setSelectedItems(selectedName);
  }, [list, props?.value]);

  return (
    <FormControl fullWidth disabled={props?.disabled ? props?.disabled : false}>
      <InputLabel
        style={{
          fontSize: '12px'
        }}
      >
        {props?.titile ? props?.titile : 'Resources'}
      </InputLabel>
      <Select
        id="filter-resource"
        sx={{ mt: 1 }}
        style={{
          fontSize: '12px',
          height: '2rem'
        }}
        multiple
        size="small"
        value={props.value || []}
        renderValue={() => selectedItems.join(', ')}
        onChange={(e) => props?.onChange(e)}
        onClose={() => setSearchText('')}
        onOpen={() => fetchLists()}
      >
        <ListSubheader>
          <TextField
            id="search-task-id"
            size="small"
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            onChange={(e) => setSearchText(e.target.value)}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') {
                e.stopPropagation();
              }
            }}
          ></TextField>
        </ListSubheader>
        {
          list?.length > 0 &&
          list.map((fl, i) => {
            return (
              <MenuItem key={fl?.employeeId} 
                value={{
                  employeeId: fl?.employeeId,
                  name: fl?.name,
                  isAllocatedResource: fl?.isAllocatedResource
                } as any}>
                  <Checkbox checked={props?.value.map(e => e.employeeId).indexOf(fl?.employeeId as never) > -1} />
                  <ListItemText primary={fl?.name} />
              </MenuItem>
            );
          })
        }
      </Select>
    </FormControl>
  );
});

export default ResourcesMultiSelect;
